import { Add, Delete, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import {
  addParameter,
  ParameterCells,
  deleteParameter,
  editParameter,
  getParameters,
} from "framework/parameter";
import { Feature } from "models/api";
import { IParameter, IParameterInput } from "models/parameter";
import React, { useState } from "react";
import { useMutation } from "react-query";
import AddParameter from "./partials/AddParameter/AddParameter";
import EditParameter from "./partials/EditParameter/EditParameter";
import DeleteParameter from "./partials/DeleteParameter/DeleteParameter";
import Search from "components/Search/Search";
import { handleError } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { getPermission, getUserData } from "helpers";
import BlockIcon from "@mui/icons-material/Block";
import useFetchSection from "hooks/useFetchSection";
import { formatDateTime } from "helpers";

interface Props {
  feature: Feature;
}

const Parameters: React.FC<Props> = ({ feature }) => {
  const [loading4, setLoading4] = useState<boolean>(true);
  const { t, i18n } = useTranslation();
  const [loading2, setLoading2] = useState<boolean>(false);
  const [FilterParameters, setFilterParameters] = useState<IParameter[]>([] as IParameter[]);
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const user = getUserData();
  const { colors } = user;
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [selectedParameter, setSelectedParameter] = useState<IParameter>({} as IParameter);
  const [inputObj, setInputObj] = useState<IParameterInput>({} as IParameterInput);
  const { setAlert } = useAlert();

  const addParameterMutation = useMutation({
    mutationFn: (createInput: IParameterInput) => {
      return addParameter(createInput);
    },
  });

  const editParameterMutation = useMutation({
    mutationFn: (data: { createInput: IParameterInput; id: string }) => {
      return editParameter(data.createInput, data.id);
    },
  });

  const deleteParameterMutation = useMutation({
    mutationFn: (id: string) => {
      return deleteParameter(id);
    },
  });

  const {
    loading,
    data: Parameters,
    error,
    permission,
    revalidate,
  } = useFetchSection({
    fetchData: () => getParameters(),
    fetchPermission: () => getPermission(61), // Parameter permission (matches feature ID and rolePermissionId)
    section: "Parameters",
    onSuccess: (data) => setFilterParameters(data),
    processResponse: (data) =>
      data.map((item: IParameter) => {
        return { ...item, id: item.id };
      }),
  });

  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  const handleOpenEdit = (Parameter: IParameter) => {
    setSelectedParameter(Parameter);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedParameter({} as IParameter);
  };

  const handleOpenDelete = (Parameter: IParameter) => {
    setSelectedParameter(Parameter);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedParameter({} as IParameter);
  };

  const getBodyCells = (row: IParameter) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "name")
        return {
          id: "name",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.name}</Typography>
            </TableCell>
          ),
        };

      if (key === "value")
        return {
          id: "value",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.value}</Typography>
            </TableCell>
          ),
        };

      if (key === "createdOn")
        return {
          id: "createdOn",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {row?.createdOn ? formatDateTime(row.createdOn) : '-'}
              </Typography>
            </TableCell>
          ),
        };

      if (key === "updatedOn")
        return {
          id: "updatedOn",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {row?.updatedOn ? formatDateTime(row.updatedOn) : '-'}
              </Typography>
            </TableCell>
          ),
        };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                justifyContent={i18n.language === "en" ? "start" : "end"}
                spacing={1}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_edit")}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}
                {permission?.hasDeletePermission && (
                  <Tooltip title={t("T_delete")}>
                    <IconButton
                      size={"small"}
                      sx={{
                        color: colors.deleteIconColor
                          ? colors.deleteIconColor
                          : "red",
                        "&:hover": { color: colors?.deleteIconHoverColor },
                      }}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
          ),
        };
      else return <></>;
    });
    return cells;
  };

  const handleAdd = async () => {
    if (!inputObj.name || !inputObj.value)
      return setAlert({
        open: true,
        message: "Please fill all fields",
        type: "error",
      });
    try {
      setLoading2(true);

      await addParameterMutation.mutateAsync(inputObj);
      setAlert({
        open: true,
        message: "Parameter added successfully",
        type: "success",
      });
      revalidate();
      handleCloseAdd();
      setInputObj({} as IParameterInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    try {
      setLoading2(true);
      let createInput: IParameterInput = {
        name: inputObj.name || selectedParameter.name,
        value: inputObj.value || selectedParameter.value,
      };
      await editParameterMutation.mutateAsync({
        createInput,
        id: selectedParameter.id,
      });
      setAlert({
        open: true,
        message: "Parameter updated successfully",
        type: "success",
      });
      revalidate();
      handleCloseEdit();
      setInputObj({} as IParameterInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteParameterMutation.mutateAsync(selectedParameter.id);
      setAlert({
        open: true,
        message: "Parameter deleted successfully",
        type: "success",
      });
      revalidate();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleSearch = async (value: string) => {
    if (!value) return setFilterParameters(Parameters!);
    let filtered = Parameters!.filter((param) =>
      (param.name + " " + param.value).toLowerCase().includes(value.toLowerCase())
    );
    setFilterParameters(filtered);
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          direction={"row"}
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Stack
            direction={"row"}
            spacing={1}
            justifyContent={"space-between"}
            width={"100%"}
          >
            <Typography
              variant="h3"
              color={colors?.labelColor ? colors?.labelColor : "header.primary"}
            >
              {t(feature.name)}
            </Typography>
            {permission?.hasModifyPermission && (
              <Tooltip title={t("T_add_parameter") || "Add Parameter"}>
                <IconButton
                  sx={{
                    color: colors.addUpdateIconColor
                      ? colors.addUpdateIconColor
                      : "red",
                    "&:hover": { color: colors?.addUpdateIconHoverColor },
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          <Stack
            direction={"row"}
            spacing={1}
            justifyContent={"space-between"}
            width={"100%"}
          >
            <Stack
              direction={{ lg: "row", xs: "column" }}
              spacing={1}
              width={"100%"}
            >
              <Search handleChange={handleSearch} placeholder={t("T_search") || "Search"} />
            </Stack>
          </Stack>
        </Grid>

        {permission?.hasReadPermission! && FilterParameters.length > 0 && (
          <List
            headCells={ParameterCells}
            data={FilterParameters}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!FilterParameters || FilterParameters.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_parameters") || "No Parameters"}
              message={t("T_no_parameters_message") || "No parameters found"}
            />
          )}

        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openAdd && (
        <AddParameter
          open={openAdd}
          loading={loading2}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditParameter
          open={openEdit}
          loading={loading2}
          parameter={selectedParameter}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}
      {openDelete && (
        <DeleteParameter
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}
    </>
  );
};

export default Parameters;

